import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import {
  clearNewGroup,
  fetchGroups,
  selectDroppedIntoGroup,
  selectGroupIsImporting,
  selectGroupsForNav,
} from '@actions/groups';
import styles from './Groups.module.scss';
import GroupIdentifierIcon from '@components/GroupIdentifierIcon';
import { LoaderIcon, PlusIcon } from '@components/Icons';
import NavSection from '../NavSection';
import KeyboardShortcut, { KeyWithTrigger } from '@components/KeyboardShortcut';
import NavigationItem from '../NavigationItem';
import navItemStyles from '../NavigationItem/NavigationItem.module.scss';
import { useDndContext, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import DraggingUI from '@components/DragAndDrop/DraggingUI';
import { useOnVisible } from '@hooks/use-on-visible';

const GroupLink = ({ group, index }) => {
  const location = useLocation();
  const isImporting = useSelector(selectGroupIsImporting(group.id));
  const droppedIntoGroup = useSelector(selectDroppedIntoGroup);
  const dndContext = useDndContext();
  const { setNodeRef, isOver } = useDroppable({
    id: isImporting ? null : group.slug || group.id,
    data: isImporting ? null : { type: 'group', group },
  });
  const isDraggingContacts = dndContext.active?.data?.current?.type == 'contactIDs';
  const to = `/group/${group.is_internal ? group.slug : group.id}`;

  let IconComponent = ({ className }) => <GroupIdentifierIcon className={cn(className, styles.groupIcon)} group={group} />;
  if (isImporting) {
    IconComponent = ({ className }) => <LoaderIcon width="10" className={cn(className, styles.groupImportingIcon)} />;
  }

  let navItem = (
    <NavigationItem
      key={group.id}
      innerRef={setNodeRef}
      className={cn(styles.listNavItem, droppedIntoGroup == group.id && styles.draggableWasDropped)}
      text={group.title}
      to={to}
      isBeingDraggedOver={isOver && isDraggingContacts}
      IconComponent={IconComponent}
      scrollToOnActive
      active={location.pathname.includes(to)}
    />
  );

  if (index > 8) {
    return navItem;
  }

  return (
    <KeyboardShortcut
      key={group.id}
      shortcut={<KeyWithTrigger keyChar={group.slug === 'starred' ? 's' : String(index + 1)} />}
    >
      <div>{navItem}</div>
    </KeyboardShortcut>
  );
};

const transformToString = (transform) => {
  if (!transform) {
    return 'none';
  }

  return `translate(${transform.x}px, ${transform.y}px)`;
};

const SortableGroupLink = ({ group, index }) => {
  const dndContext = useDndContext();
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: group.id,
    data: { type: 'group' },
  });
  const isDragging = dndContext.active?.id == group.id;
  const style = {
    transform: transformToString(transform),
    transition: !isDragging && dndContext.active ? 'transform 0.1s ease-in-out' : null,
  };

  return (
    <div
      key={group.id}
      ref={setNodeRef}
      className={cn(styles.groupLink, isDragging && styles.draggingGroup)}
      style={style}
      {...listeners}
      {...attributes}
    >
      <GroupLink group={group} index={index} />
    </div>
  );
};

const InternalGroups = () => {
  const { internal: groups } = useSelector(selectGroupsForNav);

  const renderGroup = useCallback(
    (group, index) => <GroupLink key={group.id} group={group} index={index} />,
    [],
  );

  return <>{groups.map(renderGroup)}</>;
};

const UserCreatedGroups = () => {
  const { userCreated: groups, groupOrder } = useSelector(selectGroupsForNav);
  const renderGroup = useCallback(
    (group, index) => <SortableGroupLink key={group.id} group={group} index={index} />,
    [],
  );

  return (
    <div className={cn(styles.section, styles.draggable)}>
      <SortableContext items={groupOrder} strategy={verticalListSortingStrategy}>
        {groups.map(renderGroup)}
      </SortableContext>
    </div>
  );
};

export default function Groups() {
  const dispatch = useDispatch();

  useOnVisible(() => {
    dispatch(fetchGroups({ isBackgroundSync: true }));
  });

  return (
    <NavSection className={styles.section}>
      <Link
        id="groups-section"
        to="/group/create"
        className={cn(styles.header, navItemStyles.navigationItem)}
        onClick={() => dispatch(clearNewGroup())}
      >
        <span className={cn('text-small', styles.title)}>Groups</span>
        <PlusIcon width={10} height={10} color="textPrimary" />
      </Link>
      <InternalGroups />
      <UserCreatedGroups />
      <DraggingUI />
    </NavSection>
  );
}
